import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { trackProductBridge } from '../utils/analytics';

// One reusable, intent-aware bridge from free tools/content to the core WhatsApp
// Accounting product. Copy varies by search intent so it never reads as spam.
// Factual only — no CPA / tax-filing claims.
const COPY = {
  receipt: {
    title: 'Stop processing receipts one by one',
    body: 'Scanning a receipt is only half the job. Send your receipts through WhatsApp and MoneyFeed keeps your business expense records organized all year — ready for your accountant.',
  },
  invoice: {
    title: 'Keep invoices connected to your books',
    body: 'Extracting invoice data is just the start. With MoneyFeed, your invoices and expenses stay linked to your bookkeeping — all captured through a simple WhatsApp message.',
  },
  tax: {
    title: 'Calculating tax is only part of the job',
    body: 'Estimating what you owe is useful — but accurate books need organized income and deductible-expense records all year. Send them through WhatsApp and MoneyFeed keeps your bookkeeping accountant-ready.',
  },
  mileage: {
    title: 'Keep mileage with the rest of your expenses',
    body: 'Track trips alongside your other business expenses. Send mileage and receipts through WhatsApp and MoneyFeed keeps every deductible record together and organized.',
  },
  general: {
    title: 'Keep your books organized on WhatsApp',
    body: 'MoneyFeed is AI bookkeeping on WhatsApp for freelancers and small businesses. Snap a receipt or send an expense in a message and keep your records organized and accountant-ready — all year.',
  },
};

export const ProductBridge = ({ intent = 'general', className = '' }) => {
  const c = COPY[intent] || COPY.general;
  return (
    <section className={`max-w-4xl mx-auto px-4 sm:px-8 py-8 ${className}`} data-testid="product-bridge">
      <div className="bg-gradient-to-br from-[#064E3B] to-[#065F46] rounded-3xl p-8 sm:p-10">
        <div className="flex items-center gap-2 mb-3">
          <MessageCircle className="h-5 w-5 text-[#84CC16]" />
          <span className="text-xs font-bold uppercase tracking-wider text-[#84CC16]">WhatsApp Accounting</span>
        </div>
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{c.title}</h2>
        <p className="text-[#A7F3D0] text-sm sm:text-base mb-6 max-w-2xl">{c.body}</p>
        <div className="flex flex-col sm:flex-row gap-3">
          <Link to="/whatsapp-accounting" onClick={() => trackProductBridge(intent, '/whatsapp-accounting')}>
            <Button className="bg-[#84CC16] hover:bg-[#65A30D] text-black font-bold rounded-full px-7 py-6 text-base w-full sm:w-auto" data-testid="product-bridge-cta">
              Try WhatsApp Accounting <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </Link>
          <Link to="/whatsapp-bookkeeping" onClick={() => trackProductBridge(intent, '/whatsapp-bookkeeping')}>
            <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 font-bold rounded-full px-7 py-6 text-base w-full sm:w-auto" data-testid="product-bridge-secondary">
              See how it works
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ProductBridge;
